import { isMobile } from 'react-device-detect';

import { CHAT_TIME_LIMIT_SECONDS, STORAGE_KEY_CHATS_MESSAGES_COUNT, STORAGE_KEY_RESEND_MESSAGES } from '../const';
import StorageService from '../service/StorageService';
import { store } from '../store';
import { setChatHeaderCounter } from '../store/slices/chatsSlice';

export const onLinkShareClick = (url, cb) => {
    const shareData = {
        url
    };

    if (navigator.share && isMobile) {
        navigator
            .share(shareData)
            .then(() => {
                navigator.clipboard.writeText(url).then(cb);
            })
            .catch(() => {
                navigator.clipboard.writeText(url).then(cb);
            });
    } else {
        navigator.clipboard.writeText(url).then(cb);
    }
};

export function debounce(func, wait, immediate = false) {
    let timeout;

    return function executedFunction(...args) {
        const later = function () {
            timeout = null;
            if (!immediate) {
                func(...args);
            }
        };

        const callNow = immediate && !timeout;

        clearTimeout(timeout);

        timeout = setTimeout(later, wait);

        if (callNow) {
            func(args);
        }
    };
}

export const arrayToQueryString = (key, arr) => {
    if (!Array.isArray(arr)) {
        return '';
    }

    const values = arr.map((value) => `${key}=${encodeURIComponent(value)}`).join('&');

    if (!values) {
        return '';
    }

    return values.toLowerCase();
};

export const normaliseQueryString = (string) => {
    if (!string) {
        return null;
    }
    const formattedString = string.trim().replace(/^,*(.*?),*$/, '$1');
    return formattedString;
};

export const setOverflowForSidebar = (isOpen) => {
    document.documentElement.classList.toggle('overflow-hidden', isOpen);
};

export const setRetinaClass = () => {
    if (window.devicePixelRatio > 1) {
        document.body.classList.add('retina');
    }
};

export const CheckRetinaDevice = () => {
    return window.devicePixelRatio > 1;
};

export const setMetaViewportContent = () => {
    const viewportTag = document.querySelector('#viewport');
    if (viewportTag && window.innerHeight > 650) {
        const viewportContent = viewportTag.getAttribute('content');
        viewportTag.setAttribute('content', `${viewportContent}, interactive-widget=resizes-content`);
    }
};

export const appHeight = (height) => {
    document.documentElement.style.setProperty('--app-height', `${height}px`);
};

export const setPageCssVariables = (page = '') => {
    switch (page) {
        case 'landing':
            document.documentElement.style.setProperty('--c-backgrounds', '#F7F7F7');
            document.documentElement.style.setProperty('--c-fonts', '#000000');
            break;
        case 'widget':
            document.documentElement.style.setProperty('--c-backgrounds', 'transparent');
            document.documentElement.style.setProperty('--c-fonts', '#ffffff');
            break;
        default:
            document.documentElement.style.setProperty('--c-backgrounds', '#101010');
            document.documentElement.style.setProperty('--c-fonts', '#ffffff');
    }
};

export const makeError = (message, status) => {
    let error = message instanceof Error ? message : new Error(message);
    error.status = status;
    return error;
};

export const validateChatId = (chatId) => {
    if (chatId.match(/^[0-9a-fA-F]{24}$/)) {
        return true;
    } else {
        return false;
    }
};

export const getClearString = (string) => {
    const markIndex = string.indexOf('?');

    if (markIndex !== -1) {
        return string.substring(0, markIndex);
    }

    return string;
};

export const timeCompare = (date1, date2) => {
    if (!date1 || !date2) {
        return false;
    }
    const differenceInSeconds = Math.abs((date2 - date1) / 1000);
    return !!(differenceInSeconds > CHAT_TIME_LIMIT_SECONDS);
};

export const updateMessagesLimitCounter = (chatId = '', isIncrement = true) => {
    let chatMessagesCounters = StorageService.get(STORAGE_KEY_CHATS_MESSAGES_COUNT);

    if (!chatMessagesCounters) {
        chatMessagesCounters = {
            [chatId]: 1
        };
    } else {
        const currentCount = chatMessagesCounters[chatId] || 0;
        chatMessagesCounters = {
            ...chatMessagesCounters,
            [chatId]: isIncrement ? currentCount + 1 : currentCount - 1
        };
    }

    StorageService.add(STORAGE_KEY_CHATS_MESSAGES_COUNT, chatMessagesCounters);
    store.dispatch(setChatHeaderCounter(chatMessagesCounters[chatId]));
};

export const updateResendData = (chatId = '', showResendBlockState = false, messageForResend = {}, isFulfilled = false) => {
    let resendData = StorageService.get(STORAGE_KEY_RESEND_MESSAGES) || {};

    if (messageForResend) {
        resendData = {
            ...resendData,
            [chatId]: { showResendBlockState, messageForResend }
        };
    } else {
        if (!resendData[chatId]) {
            resendData[chatId] = {};
        }
        resendData[chatId].showResendBlockState = showResendBlockState;
    }

    if (isFulfilled && resendData[chatId]) {
        delete resendData[chatId];
    }

    StorageService.add(STORAGE_KEY_RESEND_MESSAGES, resendData);
};

export const getResendData = (chatId = '') => {
    const resendData = StorageService.get(STORAGE_KEY_RESEND_MESSAGES) || {};
    return {
        showResendBlockState: resendData[`${chatId}`]?.showResendBlockState || false,
        messageForResend: resendData[`${chatId}`]?.messageForResend || ''
    };
};

export const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const shortenText = (text = '', max) => {
    if (!max || !text) {
        return text;
    }
    return text && text.length > max ? text.slice(0, max).split(' ').slice(0, -1).join(' ') + '...' : text;
};

export const splitAt = (index, str) => [str.slice(0, index), str.slice(index)];
